import { createSystem, defaultConfig } from '@chakra-ui/react'

// Theme based on custom color palette:
// Bronze Spice (#c85a1e) - warm burnt orange for primary brand
// Mauve Shadow (#483c46) - deep muted purple-brown for text/neutrals
// Dry Sage (#b8b08d) - muted sage/tan for backgrounds
// Cerulean (#40798c) - teal-blue for accents
// Dark Spruce (#2f4b26) - deep forest green for success/nature elements

const theme = createSystem(defaultConfig, {
  theme: {
    tokens: {
      colors: {
        // Primary brand color - Bronze Spice
        brand: {
          50: { value: '#FFF8F3' },
          100: { value: '#FEEEE3' },
          200: { value: '#FDD4BA' },
          300: { value: '#F5AF7E' },
          400: { value: '#E89B6A' },
          500: { value: '#D97A44' }, // Bronze Spice - main brand color (softened)
          600: { value: '#C85A1E' },
          700: { value: '#A84817' },
          800: { value: '#853813' },
          900: { value: '#642A0E' },
        },
        // Mauve Shadow - for text and dark neutrals
        mauve: {
          50: { value: '#F5F4F5' },
          100: { value: '#E8E6E7' },
          200: { value: '#D1CDD0' },
          300: { value: '#B4ADB2' },
          400: { value: '#8E8389' },
          500: { value: '#6F656C' },
          600: { value: '#483C46' }, // Mauve Shadow - deep neutral
          700: { value: '#3D3339' },
          800: { value: '#352B31' },
          900: { value: '#2D2429' },
        },
        // Dry Sage - for backgrounds and soft accents
        sage: {
          50: { value: '#FDFDFB' },
          100: { value: '#F7F6F0' },
          200: { value: '#ECEBD9' },
          300: { value: '#DEDCBE' },
          400: { value: '#CCC69F' },
          500: { value: '#B8B08D' }, // Dry Sage - neutral background
          600: { value: '#9A9375' },
          700: { value: '#79735D' },
          800: { value: '#5A5546' },
          900: { value: '#3A3829' },
        },
        // Cerulean - teal-blue accent
        cerulean: {
          50: { value: '#F2F8FA' },
          100: { value: '#E0F0F4' },
          200: { value: '#B8DDE6' },
          300: { value: '#88C4D4' },
          400: { value: '#5FA1B8' },
          500: { value: '#40798C' }, // Cerulean - teal accent
          600: { value: '#366B7D' },
          700: { value: '#2F5D6E' },
          800: { value: '#29505F' },
          900: { value: '#234350' },
        },
        // Dark Spruce - forest green for success/nature
        spruce: {
          50: { value: '#F4F7F3' },
          100: { value: '#E5EDE3' },
          200: { value: '#C8D9C3' },
          300: { value: '#A3BF9C' },
          400: { value: '#729E6B' },
          500: { value: '#4F7847' },
          600: { value: '#2F4B26' }, // Dark Spruce - deep forest green
          700: { value: '#28422A' },
          800: { value: '#23392A' },
          900: { value: '#1E3028' },
        },
        // Byte Pantry design system palette
        bpInk: { value: '#1c1c1a' },
        bpTeal: {
          dark: { value: '#0f4040' },
          base: { value: '#1a6b6b' },
          light: { value: '#2a9090' },
        },
        bpAmber: {
          base: { value: '#e8a020' },
          light: { value: '#f5c05a' },
        },
        bpCream: {
          dark: { value: '#ddd5c2' },
          base: { value: '#ede8de' },
          warm: { value: '#f0ebe0' },
        },
        bpWarmWhite: { value: '#faf7f2' },
      },
      radii: {
        sm: { value: '4px' },
        md: { value: '8px' },
        lg: { value: '12px' },
        full: { value: '9999px' }
      },
      fonts: {
        body: { value: '"DM Sans", system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' },
        heading: { value: '"Jost", "DM Sans", system-ui, sans-serif' },
        mono: { value: 'ui-monospace, SFMono-Regular, Menlo, monospace' }
      },
      shadows: {
        xs: { value: '0 1px 2px rgba(0,0,0,0.04)' },
        sm: { value: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' },
        md: { value: '0 4px 8px rgba(0,0,0,0.06)' },
      }
    },
    semanticTokens: {
      colors: {
        'bg.canvas': { value: '{colors.sage.50}' },
        'bg.surface': { value: '{colors.sage.100}' },
        'bg.muted': { value: '{colors.sage.200}' },
        'border.muted': { value: '{colors.sage.300}' },
        'text.primary': { value: '{colors.mauve.600}' },
        'text.muted': { value: '{colors.mauve.400}' },
        'text.subtle': { value: '{colors.mauve.300}' },
        'accent.default': { value: '{colors.brand.500}' },
        'accent.emphasis': { value: '{colors.brand.600}' },
        'accent.subtle': { value: '{colors.brand.50}' },
        'accent.emphasisHover': { value: '{colors.brand.700}' },
        'accent.secondary': { value: '{colors.cerulean.500}' },
        'accent.secondaryHover': { value: '{colors.cerulean.600}' },
        'accent.secondarySubtle': { value: '{colors.cerulean.100}' },
        'accent.secondaryBorder': { value: '{colors.cerulean.200}' },
        'status.success': { value: '{colors.spruce.600}' },
        'status.error': { value: '{colors.red.500}' },
      },
      radii: {
        'radius.surface': { value: '{radii.md}' }
      }
    },
  },
})

export default theme
